/** Slash actions and assigned skills shown by the workspace composer. */
import { listAgentTemplates } from "@/api/agents";
import { updateMaxParallelAgents } from "@/api/auth";
import { runVoiceCommand } from "@/desktop/voice-read-aloud-command";
import { isTauri } from "@/lib/is-tauri";
import { requestStandaloneNewChat } from "@/lib/new-chat-command";
import { ALL_BUILT_IN, type SlashCommand } from "@/lib/slash-commands";
import { useAgentStore } from "@/stores/agent-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { translate, useAppLanguage } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import { useNotificationStore } from "@/stores/notification-store";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import { useMemo } from "react";
import type { useMasterLayoutShell } from "./use-master-layout-shell";

export function useMasterSlashCommands({
  shell,
  panToFrame,
  runTidy,
  handleFocusMode,
  handleReviewMode,
  projectAgents,
  agentTemplates,
  skillRegistry,
}: {
  shell: ReturnType<typeof useMasterLayoutShell>;
  panToFrame: ReturnType<typeof useCanvasStore.getState>["panToFrame"];
  runTidy: () => void;
  handleFocusMode: () => void;
  handleReviewMode: () => void;
  projectAgents: ReturnType<typeof useAgentStore.getState>["agents"];
  agentTemplates: ReturnType<typeof useAgentStore.getState>["templates"];
  skillRegistry: ReturnType<typeof useAgentStore.getState>["skills"];
}): SlashCommand[] {
  const { data, handleTaskboard, liveStatus, refreshLiveStatus } = shell;
  const voiceOn = useTtsSettingsStore((s) => s.enabled);
  // Descriptions and statuses are read in the app language when built.
  const language = useAppLanguage();
  const slashCommands: SlashCommand[] = useMemo(() => {
    const handlers: Record<string, (arg?: string) => void> = {
      new: requestStandaloneNewChat,
      tidy: runTidy,
      focus: handleFocusMode,
    };

    // /board — navigate to existing taskboard or create one
    handlers.board = handleTaskboard;
    // /review — toggle review presentation mode for unread agent updates
    handlers.review = handleReviewMode;
    // /heart — toggle heartbeat on/off for all agents
    handlers.heart = async () => {
      const { toggleHeartbeat } = await import("@/api/agents");
      try {
        const templates = await listAgentTemplates();
        const anyEnabled = templates.some(
          (t) => t.adapter_config?.heartbeat_enabled,
        );
        const newState = !anyEnabled;
        const result = await toggleHeartbeat(newState);
        useNotificationStore.getState().addSystemToast({
          id: `heartbeat-toggle-${Date.now()}`,
          level: "info",
          title: translate(
            layoutMessages,
            newState ? "heartbeatOn" : "heartbeatOff",
          ),
          description: translate(layoutMessages, "agentsUpdated", {
            count: result.agents_updated,
          }),
        });
        await refreshLiveStatus();
      } catch {
        useNotificationStore.getState().addSystemToast({
          id: `heartbeat-toggle-fail-${Date.now()}`,
          level: "error",
          title: translate(layoutMessages, "heartbeatFailed"),
        });
      }
    };

    // /voice [on|off] — Denker's voice, in the desktop app (`voice-read-aloud-command.ts`).
    handlers.voice = (arg?: string) => {
      if (runVoiceCommand(`/voice ${arg ?? ""}`.trim())) return;
      useNotificationStore.getState().addSystemToast({
        id: `voice-usage-${crypto.randomUUID()}`,
        level: "error",
        title: translate(layoutMessages, "voiceUsage"),
      });
    };

    // /max <N> — set max parallel agents (1–10).
    handlers.max = async (arg?: string) => {
      const value = arg ? parseInt(arg.trim(), 10) : NaN;
      if (!Number.isFinite(value) || value < 1 || value > 10) {
        useNotificationStore.getState().addSystemToast({
          id: `max-invalid-${crypto.randomUUID()}`,
          level: "error",
          title: translate(layoutMessages, "maxUsage"),
        });
        return;
      }
      try {
        await updateMaxParallelAgents(value);
        await refreshLiveStatus();
        useNotificationStore.getState().addSystemToast({
          id: `max-set-${crypto.randomUUID()}`,
          level: "info",
          title: translate(layoutMessages, "maxSet", { value }),
        });
      } catch {
        useNotificationStore.getState().addSystemToast({
          id: `max-set-fail-${crypto.randomUUID()}`,
          level: "error",
          title: translate(layoutMessages, "maxFailed"),
        });
      }
    };

    const builtIn = ALL_BUILT_IN.filter(
      (meta) => meta.name !== "voice" || isTauri(),
    );
    const commands: SlashCommand[] = builtIn.map((meta) => {
      const enriched: SlashCommand = {
        ...meta,
        handler: handlers[meta.name],
      };
      if (meta.name === "max") {
        enriched.status = translate(layoutMessages, "currently", {
          value: liveStatus.maxParallel,
        });
      } else if (meta.name === "heart") {
        enriched.status = translate(
          layoutMessages,
          liveStatus.heartbeatOn ? "on" : "off",
        );
      } else if (meta.name === "voice") {
        enriched.status = translate(layoutMessages, voiceOn ? "on" : "off");
      }
      return enriched;
    });

    // Build skills from the currently-selected agent's assigned skills.
    // Selection is owned by useMasterLayoutData via data.selectedAgentIds;
    // the first id is treated as the "focused" agent (matches focusedAgent).
    const ids = data.selectedAgentIds;
    const firstId = ids && ids.size > 0 ? [...ids][0] : null;
    const pa = firstId
      ? projectAgents.find((a) => a.id === firstId)
      : undefined;
    const template = pa
      ? agentTemplates.find((t) => t.id === pa.agent_template)
      : undefined;
    const assignedSlugs = template?.adapter_config?.skills ?? [];
    const agentSkillCommands: SlashCommand[] = assignedSlugs.flatMap((slug) => {
      const skill = skillRegistry.find((s) => s.slug === slug);
      if (!skill) return [];
      return [
        {
          name: skill.slug,
          description: skill.description || skill.name,
          icon: "Sparkles",
          type: "skill" as const,
        },
      ];
    });

    return [...commands, ...agentSkillCommands];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    panToFrame,
    handleTaskboard,
    runTidy,
    handleFocusMode,
    handleReviewMode,
    liveStatus,
    refreshLiveStatus,
    voiceOn,
    data.selectedAgentIds,
    projectAgents,
    agentTemplates,
    skillRegistry,
    language,
  ]);

  return slashCommands;
}
